import * as React from "react"
import { FaExpand } from "react-icons/fa"
import { FullScreenButton } from "../custom-player.styled"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import { isBigPlayIconState } from "../../../../recoil/atoms/bigPlayIcon"
import { useSetRecoilState, useRecoilValue } from "recoil"
import { playerPropsSelector } from "../../../../recoil/atoms/player"
import { waitForElm } from "../../../../util/waitForElm"
import { FullScreenPlayerControlButtonProps } from "../custom-player.interface"
import { useWatchParty } from "../../../../hooks/useWatchParty"
import { useFullScreenMobile } from "../../../../hooks/useFullScreenMobile"
import { viewDependingLayoutAtom } from "../../../../recoil/atoms/view"

const _FullScreenPlayerControlButton: React.FC<FullScreenPlayerControlButtonProps> =
  ({
    playerRef,
    watchPartyHostId,
    touchDelay,
    isMobile,
    isTablet,
    hideControls,
    isPanelFS
  }): JSX.Element => {
    const { watchPartyType } = useWatchParty()
    const { isLandscape, isTransparent } = useRecoilValue(
      viewDependingLayoutAtom
    )
    const { handleFullScreenMobileWebCam } = useFullScreenMobile()

    const [timeStamp, setTimeStamp] = React.useState({
      start: 0,
      end: 0
    })
    const { isAndroid, isIOS } = useBrowserType()
    const setPlayerProps = useSetRecoilState(playerPropsSelector)
    const setBigPlayIcon = useSetRecoilState(isBigPlayIconState)

    const toggleBigPlayButton = React.useCallback(() => {
      let timer = setTimeout(() => {
        setBigPlayIcon(true)
      }, 1000)
      return () => clearTimeout(timer)
    }, [setBigPlayIcon])

    const handleTouchStart = e => {
      setTimeStamp({ ...timeStamp, start: e.timeStamp })
    }

    const handleOnTouchEndFullScreen = e => {
      if (e.timeStamp - timeStamp.start < touchDelay) {
        handleFullScreen()
        if (!isAndroid) {
          toggleBigPlayButton()
        }
        if (!isAndroid) {
          setPlayerProps({ paused: true })
        }
      }
      setTimeStamp({ start: 0, end: 0 })
    }

    const isInFullScreen =
      (document.fullscreenElement && document.fullscreenElement !== null) ||
      (document.webkitFullscreenElement &&
        document.webkitFullscreenElement !== null) ||
      (document.mozFullScreenElement &&
        document.mozFullScreenElement !== null) ||
      (document.msFullscreenElement && document.msFullscreenElement !== null)

    const handleFullScreen = React.useCallback(async () => {
      if (watchPartyType === "audio" && isMobile) {
        handleFullScreenMobileWebCam()
        if (isIOS) {
          toggleBigPlayButton()
        }
      } else {
        const video = (await waitForElm(
          "watchpartyvideoplayer"
        )) as HTMLVideoElement
        if (!isInFullScreen) {
          if (playerRef.current.requestFullScreen) {
            // W3C API

            playerRef.current.requestFullScreen()
          } else if (playerRef.current.mozRequestFullScreen) {
            // Mozilla current API
            playerRef.current.mozRequestFullScreen()
          } else if (playerRef.current.webkitRequestFullScreen) {
            // Webkit current API
            playerRef.current.webkitRequestFullScreen()
          } else if (video.webkitEnterFullScreen) {
            // This is the IOS Mobile edge case
            if (watchPartyHostId !== "modNotPresentVideo" && !isAndroid) {
              toggleBigPlayButton()
            }
            if (!isAndroid) {
              
              setPlayerProps({ paused: true })
            }
            video.webkitEnterFullScreen()
          }
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen()
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen()
          } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen()
          } else if (document.msExitFullscreen) {
            document.msExitFullscreen()
          }
        }
      }
    }, [
      handleFullScreenMobileWebCam,
      isAndroid,
      isIOS,
      isInFullScreen,
      isMobile,
      playerRef,
      setPlayerProps,
      toggleBigPlayButton,
      watchPartyHostId,
      watchPartyType
    ])

    return (
      <FullScreenButton
        onClick={handleFullScreen}
        isMobile={isMobile}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleOnTouchEndFullScreen}
        style={{
          // padding: hideControls && (isLandscape ? "0px 2px 0px 0px" : "0px")
        }}
        isPanelFS={isPanelFS}
        isLandscape={isLandscape}
        isTransparent={isTransparent}
      >
        <FaExpand
          size="1.9rem"
          style={{
            borderRadius: "0.375rem",
            background: "rgba(0, 0, 0, 0.2)",
            padding: "3px"
          }}
        />
      </FullScreenButton>
    )
  }

export const FullScreenPlayerControlButton = React.memo(
  _FullScreenPlayerControlButton
)
